"use client";

import Image from "next/image";
import { usePathname, useRouter, useSearchParams } from "next/navigation";

import cn from "classnames";

import { ChevronRightIcon } from "@/components/icons/chevronRight.icon";
import { Text } from "@/components/text/text.component";

import { PRODUCT_DRAWER_QUERY_PARAM } from "@/constants/product.constants";

import { AnswerProps } from "../quizFlow.types";
import { QuizQuestionHelperText } from "./quizQuestionHelperText.component";

export const SingleChoice = ({
  question,
  onSingleChoiceSelect,
  initialAnswer,
}: AnswerProps) => {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const options = question.options ?? [];
  const hasAnyIcon = options.some((o) => o.icon?.asset);
  const isTextOnly = !hasAnyIcon;
  const savedAnswer = typeof initialAnswer === "string" ? initialAnswer : null;

  const openProductDrawer = (productId: string) => {
    const params = new URLSearchParams(searchParams.toString());
    params.set(PRODUCT_DRAWER_QUERY_PARAM, productId);
    router.push(`${pathname}?${params.toString()}`);
  };

  const renderOptions = () =>
    options.map((option) => {
      const iconUrl = option.icon?.asset?.url ?? null;
      const key = option._key ?? option.title;
      const isSelected =
        savedAnswer === option.title ||
        savedAnswer === key ||
        (!!option.product?.id && savedAnswer === option.product.id);

      if (isTextOnly) {
        return (
          <button
            key={key}
            data-testid="single-choice-option"
            onClick={() => onSingleChoiceSelect(key)}
            className={cn(
              "border-greyscale-strength rounded-xl border px-6 py-4 text-left transition-colors",
              isSelected
                ? "border-secondary bg-primary/10"
                : "hover:border-secondary focus:border-secondary",
            )}>
            <Text variant="body4" className="text-greyscale-headline font-thin">
              {option.title}
            </Text>
          </button>
        );
      }

      const productId =
        question.allowProductDrawer && option.product?.id
          ? option.product.id
          : null;

      return (
        <button
          key={key}
          data-testid="single-choice-option"
          onClick={() => onSingleChoiceSelect(key)}
          className={cn(
            "border-greyscale-strength flex items-center gap-4 rounded-xl border p-5 text-left transition-colors",
            isSelected
              ? "border-secondary bg-primary/10"
              : "hover:border-secondary focus:border-secondary",
          )}>
          {iconUrl && (
            <div className="relative mt-0.5 h-16 w-16 shrink-0 overflow-hidden rounded-lg">
              <Image
                src={iconUrl}
                alt={option.title}
                fill
                className="object-contain"
                sizes="40px"
              />
            </div>
          )}
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-1">
              {productId ? (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    openProductDrawer(productId);
                  }}
                  className="text-greyscale-headline body4 text-left underline decoration-[0.5px] underline-offset-6">
                  {option.title}
                </button>
              ) : (
                <Text variant="body4" className="text-greyscale-headline">
                  {option.title}
                </Text>
              )}
              {productId && (
                <ChevronRightIcon
                  width={16}
                  height={16}
                  className="text-greyscale-strength"
                />
              )}
            </div>
            {option.description && (
              <Text variant="body6" className="font-thin">
                {option.description}
              </Text>
            )}
          </div>
        </button>
      );
    });

  return (
    <div
      data-testid="single-choice"
      className="mx-auto flex w-full max-w-2xl flex-col gap-3 lg:min-w-2xl">
      {renderOptions()}
      <QuizQuestionHelperText text={question.helperText} />
    </div>
  );
};
